import { useEffect, useRef, useState, useCallback } from "react";

import Hls from "hls.js";

import api from "@helpers/api";
import { useAuth } from "@contexts/AuthContext";

import { saveVideoProgress, getStartPosition } from "../stores/progressStore";

const REPORT_INTERVAL_MS = 5000;
const WATCHED_THRESHOLD_PERCENT = 80;

/**
 * usePlayerEngine — media pipeline + persistence for one content video.
 *
 * Owns everything that is NOT about the visible controls:
 *  - hls.js attach/teardown for the HLS manifest
 *  - resume position (localStorage) + initial play()
 *  - periodic progress persistence and view reporting to the API
 *  - watched state (manual toggle + auto-mark at 90%)
 *  - `ended` notification for the autoplay flow
 *
 * All frequent values live in refs; React state is only touched for `watched`
 * and `needsPlayGesture` (autoplay-with-sound blocked → wait for a tap).
 *
 * `resume=false` always starts at zero (the welcome-video modal opens from a
 * muted preview and must not pick up where a previous session left off);
 * progress is still saved for the Content page.
 */
export default function usePlayerEngine({ info, videoRef, onEnded, resume = true }) {
    const { user } = useAuth();

    const [watched, setWatched] = useState(!!info.watched);
    const [needsPlayGesture, setNeedsPlayGesture] = useState(false);
    // The live Hls instance as state (not just a ref): useCaptions must
    // re-attach its subtitle listeners whenever the engine rebuilds it.
    const [hlsInstance, setHlsInstance] = useState(null);

    const hlsRef = useRef(null);
    const reportIntervalRef = useRef(null);
    const lastReportedMillisRef = useRef(0);

    // Refs mirroring reactive values so media listeners never hold stale closures.
    const watchedRef = useRef(watched);
    watchedRef.current = watched;
    const reachedThresholdRef = useRef(false);
    const userIdRef = useRef(user?.id);
    userIdRef.current = user?.id;
    const onEndedRef = useRef(onEnded);
    onEndedRef.current = onEnded;

    const videoSrc = info.video?.src;
    const contentId = info.id;

    const registerWatchedStatus = useCallback(async (state) => {
        setWatched(state);
        try {
            await api.post(`content/${contentId}/view/watched`, { state: !!state });
        } catch (error) {
            console.error("Erro ao registrar status de visualização:", error);
        }
    }, [contentId]);

    const toggleWatched = useCallback(() => {
        registerWatchedStatus(!watchedRef.current);
    }, [registerWatchedStatus]);

    const saveCurrentProgress = useCallback(() => {
        const video = videoRef.current;
        if (!video || !video.duration || isNaN(video.duration)) return;
        saveVideoProgress(contentId, Math.floor(video.currentTime * 1000));
    }, [contentId, videoRef]);

    const registerContentView = useCallback(async (position, millisIncrease) => {
        try {
            await api.post(`content/${contentId}/view/progress`, {
                position,
                millis_increase: millisIncrease,
                state: true,
            });
        } catch (error) {
            console.error("Erro ao registrar visualização do conteúdo:", error);
        }
    }, [contentId]);

    const stopReporting = useCallback(() => {
        if (reportIntervalRef.current) {
            clearInterval(reportIntervalRef.current);
            reportIntervalRef.current = null;
        }
    }, []);

    // While playing: every 5s persist the resume position locally and, for
    // authenticated users, report the watched-time increase to the API.
    const startReporting = useCallback(() => {
        stopReporting();

        const video = videoRef.current;
        if (video) lastReportedMillisRef.current = Math.floor(video.currentTime * 1000);

        reportIntervalRef.current = setInterval(() => {
            const current = videoRef.current;
            if (!current || current.paused || current.ended) return;

            saveCurrentProgress();

            if (!userIdRef.current) return;
            const currentMillis = Math.floor(current.currentTime * 1000);
            const increase = currentMillis - lastReportedMillisRef.current;
            if (increase > 0) {
                registerContentView(currentMillis, increase);
                lastReportedMillisRef.current = currentMillis;
            }
        }, REPORT_INTERVAL_MS);
    }, [videoRef, saveCurrentProgress, registerContentView, stopReporting]);

    // Media pipeline + persistence listeners, one attach per source.
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoSrc) return;

        const hls = new Hls({
            maxBufferLength: 30,
            maxMaxBufferLength: 60,
            liveSyncDuration: 10,
            enableWorker: true,
            lowLatencyMode: true,
            // hls.js defaults to true and auto-renders any DEFAULT=YES
            // rendition — the default-subtitle rule in useCaptions must be
            // the only thing that turns captions on.
            subtitleDisplay: false,
        });
        hlsRef.current = hls;
        setHlsInstance(hls);
        hls.loadSource(videoSrc);
        hls.attachMedia(video);

        const handlePlay = () => {
            setNeedsPlayGesture(false);
            startReporting();
        };

        const handlePause = () => {
            saveCurrentProgress();
            stopReporting();
        };

        const handleEnded = () => {
            saveCurrentProgress();
            stopReporting();
            onEndedRef.current?.();
        };

        const handleTimeUpdate = () => {
            if (!video.duration) return;
            const progress = (video.currentTime / video.duration) * 100;
            if (progress >= WATCHED_THRESHOLD_PERCENT && !reachedThresholdRef.current && !watchedRef.current) {
                reachedThresholdRef.current = true;
                registerWatchedStatus(true);
            }
        };

        const handleBeforeUnload = () => {
            saveCurrentProgress();
            stopReporting();
        };

        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        video.addEventListener("ended", handleEnded);
        video.addEventListener("timeupdate", handleTimeUpdate);
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            hls.destroy();
            hlsRef.current = null;
            setHlsInstance(null);
            stopReporting();
            video.removeEventListener("play", handlePlay);
            video.removeEventListener("pause", handlePause);
            video.removeEventListener("ended", handleEnded);
            video.removeEventListener("timeupdate", handleTimeUpdate);
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [videoSrc, videoRef, startReporting, stopReporting, saveCurrentProgress, registerWatchedStatus]);

    // Resume where the viewer left off, then start playback. The stored
    // position is validated against the duration, so seek only after metadata.
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoSrc) return;

        let retryTimer = null;

        // Genuinely blocked (fresh session, zero interaction — no site can
        // autoplay with sound there): stay paused and unmuted, surface the
        // centered play affordance, and let the user's tap start playback
        // with audio.
        const waitForPlayGesture = () => {
            video.muted = false;
            setNeedsPlayGesture(true);
        };

        const applyResume = () => {
            const startPosition = resume ? getStartPosition(contentId, video.duration) : 0;
            if (startPosition > 0) video.currentTime = startPosition;

            // Play unmuted. A NotAllowedError while the user HAS already
            // interacted with the page (sticky activation) is usually a
            // transient race with the media pipeline — retry once before
            // conceding to click-to-play. AbortErrors from a source swap
            // must NOT trigger the fallback.
            video.muted = false;
            video.play().catch((error) => {
                if (error?.name !== "NotAllowedError") return;

                if (navigator.userActivation?.hasBeenActive) {
                    retryTimer = setTimeout(() => {
                        video.muted = false;
                        video.play().catch((retryError) => {
                            if (retryError?.name === "NotAllowedError") waitForPlayGesture();
                        });
                    }, 300);
                    return;
                }

                waitForPlayGesture();
            });
        };

        if (video.readyState >= 1) {
            applyResume();
        } else {
            video.addEventListener("loadedmetadata", applyResume, { once: true });
        }

        return () => {
            video.removeEventListener("loadedmetadata", applyResume);
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [videoSrc, contentId, videoRef, resume]);

    return { watched, toggleWatched, needsPlayGesture, hls: hlsInstance };
}
